/* eslint-disable no-console */
import {
  isBRServer,
} from '@/utils/Validators/validation';

// Constantes de tamanho de documento (sem máscara)
export const CPF_LENGTH = 11;
export const CNPJ_LENGTH = 14;

// Constantes de tamanho de documento (com máscara)
export const CPF_MASKED_LENGTH = 14; // XXX.XXX.XXX-XX
export const CNPJ_MASKED_LENGTH = 18; // XX.XXX.XXX/XXXX-XX

export const isAlphaNumeric = (value) => {
  const regex = /^[a-zA-Z0-9]+$/;
  return regex.test(value);
};

export const validateCpf = (value) => {
  const cpf = value.replace(/[^\d]+/g, '');
  if (cpf.length > CPF_LENGTH) return true;
  if (cpf === '') return false;
  // Elimina CPFs invalidos conhecidos
  if (cpf.length !== CPF_LENGTH
    || cpf === '00000000000'
    || cpf === '11111111111'
    || cpf === '22222222222'
    || cpf === '33333333333'
    || cpf === '44444444444'
    || cpf === '55555555555'
    || cpf === '66666666666'
    || cpf === '77777777777'
    || cpf === '88888888888'
    || cpf === '99999999999') {
    return false;
  }
  let add;
  let rev;
  let i;
  // Valida 1o digito
  add = 0;
  for (i = 0; i < 9; i += 1) {
    add += parseInt(cpf.charAt(i), 10) * (10 - i);
  }
  rev = 11 - (add % 11);
  if (rev === 10 || rev === 11) {
    rev = 0;
  }
  if (rev !== parseInt(cpf.charAt(9), 10)) {
    return false;
  }
  // Valida 2o digito
  add = 0;
  for (i = 0; i < 10; i += 1) {
    add += parseInt(cpf.charAt(i), 10) * (11 - i);
  }
  rev = 11 - (add % 11);
  if (rev === 10 || rev === 11) {
    rev = 0;
  }
  if (rev !== parseInt(cpf.charAt(10), 10)) {
    return false;
  }
  return true;
};

/**
 * Valida CNPJ (numérico ou alfanumérico)
 * @param {string} value - CNPJ com ou sem máscara
 * @param {boolean} isCNPJAlphanumeric - Se true, valida CNPJ alfanumérico (módulo 11 com ASCII-48)
 * @returns {boolean} True se válido
 */
export const validateCnpj = (value, isCNPJAlphanumeric = false) => {
  const regex = isCNPJAlphanumeric ? /[^a-zA-Z0-9]+/g : /[^\d]+/g;
  const cnpj = value.replace(regex, '');

  // Valida a quantidade de caracteres
  if (cnpj.length !== CNPJ_LENGTH) {
    return false;
  }

  // Para CNPJ alfanumérico, usa módulo 11 convertendo caracteres por ASCII-48
  if (isCNPJAlphanumeric) {
    // Validação de formato básico: exatamente 14 caracteres alfanuméricos
    if (!/^[a-zA-Z0-9]{14}$/.test(cnpj)) {
      return false;
    }

    const cnpjUpper = cnpj.toUpperCase();

    // Valida que os últimos 2 caracteres são dígitos (verificadores devem ser numéricos)
    const lastTwo = cnpjUpper.substring(12, 14);
    if (!/^\d{2}$/.test(lastTwo)) {
      return false;
    }

    // Elimina padrões inválidos: todos os caracteres iguais (ex: AAAAAAAAAAAAAA)
    if (/^(.)\1{13}$/.test(cnpjUpper)) {
      return false;
    }

    /**
     * Converte caractere para valor numérico usando ASCII-48
     * '0'-'9' → 0-9 (ASCII 48-57, menos 48 = 0-9)
     * 'A'-'Z' → 17-42 (ASCII 65-90, menos 48 = 17-42)
     * @throws {Error} Se caractere inválido (proteção adicional)
     */
    const charToValue = (char) => {
      const asciiValue = char.charCodeAt(0) - 48;
      // Validação de range: '0'(0) até 'Z'(42)
      // '0'=48-48=0, '9'=57-48=9, 'A'=65-48=17, 'Z'=90-48=42
      if (asciiValue < 0 || asciiValue > 42) {
        // Não deveria acontecer devido ao regex, mas proteção extra
        return false;
      }
      return asciiValue;
    };

    /**
     * Calcula dígito verificador usando módulo 11
     * @param {string} base - Parte do CNPJ para calcular
     * @param {number[]} weights - Pesos para multiplicação
     * @returns {number} Dígito verificador calculado (0-9)
     */
    const calcDigit = (base, weights) => {
      const sum = base
        .split('')
        .reduce((accumulator, currentChar, index) => {
          const value = charToValue(currentChar);
          // Se charToValue retornar false (caractere inválido), invalida o CNPJ
          if (value === false) return NaN;
          return accumulator + (value * weights[index]);
        }, 0);

      // Se sum é NaN, houve caractere inválido
      // eslint-disable-next-line no-restricted-globals
      if (isNaN(sum)) return -1;

      const result = 11 - (sum % 11);
      return result > 9 ? 0 : result;
    };

    // Base: primeiros 12 caracteres
    const base = cnpjUpper.substring(0, 12);

    // Calcula primeiro dígito verificador (peso decrescente de 5 a 2, depois 9 a 2)
    const firstDigit = calcDigit(base, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);

    // Calcula segundo dígito verificador (inclui primeiro DV, peso decrescente de 6 a 2, depois 9 a 2)
    const secondDigit = calcDigit(`${base}${firstDigit}`, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);

    // Se algum cálculo falhou, CNPJ inválido
    if (firstDigit === -1 || secondDigit === -1) {
      return false;
    }

    // Dígitos informados no CNPJ
    const informedFirstDigit = parseInt(cnpjUpper.charAt(12), 10);
    const informedSecondDigit = parseInt(cnpjUpper.charAt(13), 10);

    // Valida que ambos os dígitos batem
    return firstDigit === informedFirstDigit && secondDigit === informedSecondDigit;
  }

  // Validação tradicional para CNPJ numérico
  // Elimina inválidos com todos os caracteres iguais
  if (/^(\d)\1+$/.test(cnpj)) {
    return false;
  }

  // Cálculo de validação
  const t = cnpj.length - 2;
  const d = cnpj.substring(t);
  const d1 = parseInt(d.charAt(0), 10);
  const d2 = parseInt(d.charAt(1), 10);
  const calc = (x) => {
    const n = cnpj.substring(0, x);
    let y = x - 7;
    let s = 0;
    let r = 0;

    for (let i = x; i >= 1; i -= 1) {
      // eslint-disable-next-line no-plusplus
      s += n.charAt(x - i) * y--;
      if (y < 2) {
        y = 9;
      }
    }

    r = 11 - (s % 11);
    return r > 9 ? 0 : r;
  };

  return calc(t) === d1 && calc(t + 1) === d2;
};

/**
 * Retorna o tamanho do documento (CPF/CNPJ) sem máscara
 * @param {string} value - Documento com ou sem máscara
 * @param {boolean} isCNPJAlphanumeric - Se true, aceita CNPJ alfanumérico (letras + números)
 * @returns {number} Tamanho do documento apenas com caracteres válidos
 */
export const cnpjCpfSize = (value, isCNPJAlphanumeric = false) => {
  const regex = isCNPJAlphanumeric ? /[^a-zA-Z0-9]+/g : /[^\d]+/g;
  const cleanValue = value.replace(regex, '');
  return cleanValue.length;
};

/**
 * Formata CPF ou CNPJ para exibição com máscara
 * @param {string} value - Documento sem formatação
 * @param {boolean} isCNPJAlphanumeric - Se true, aceita CNPJ alfanumérico (letras + números)
 * @returns {string} Documento formatado (CPF: 000.000.000-00 ou CNPJ: XX.XXX.XXX/XXXX-XX)
 */
export const formatCpfCnpj = (value, isCNPJAlphanumeric = false) => {
  const regex = isCNPJAlphanumeric ? /[^a-zA-Z0-9]+/g : /[^\d]+/g;
  const cleanValue = value.replace(regex, '');

  if (cleanValue.length === CPF_LENGTH) {
    // CPF: 000.000.000-00
    return cleanValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  if (cleanValue.length === CNPJ_LENGTH) {
    // CNPJ: XX.XXX.XXX/XXXX-XX (numérico ou alfanumérico)
    const pattern = isCNPJAlphanumeric
      ? /([a-zA-Z0-9]{2})([a-zA-Z0-9]{3})([a-zA-Z0-9]{3})([a-zA-Z0-9]{4})([a-zA-Z0-9]{2})/
      : /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/;
    return cleanValue.replace(pattern, '$1.$2.$3/$4-$5');
  }

  return value;
};

/**
 * Determina se é empresa baseado no documento e contexto
 * @param {string} document - Documento a verificar
 * @param {boolean} isForeigner - Se o usuário é estrangeiro
 * @param {string} companyName - Nome da empresa (se houver)
 * @param {boolean} isCNPJAlphanumeric - Se true, aceita CNPJ alfanumérico
 * @returns {boolean} True se for empresa
 */
export const determineIsCompany = (document, isForeigner = false, companyName = '', isCNPJAlphanumeric = false) => {
  const documentSize = cnpjCpfSize(document, isCNPJAlphanumeric);

  // No Brasil, estrangeiros não são considerados empresa automaticamente
  if (isBRServer && isForeigner) {
    return false;
  }

  // No Brasil, se não é estrangeiro, verifica pelo documento
  if (isBRServer && !isForeigner) {
    return documentSize === CNPJ_LENGTH;
  }

  // Em outros países, verifica se tem nome de empresa
  if (!isBRServer && companyName) {
    return true;
  }

  return false;
};

/**
 * Valida documento (CPF ou CNPJ) baseado no contexto do servidor e se é estrangeiro
 * @param {string} document - Documento a ser validado
 * @param {boolean} isForeigner - Se o usuário é estrangeiro
 * @param {function} setIsCompany - Função opcional para definir se é empresa
 * @param {boolean} isCNPJAlphanumeric - Se true, aceita CNPJ alfanumérico
 * @returns {string|boolean} Código de erro ('cpf', 'cnpj', 'invalidDocument') ou false se válido
 */
export const validateDocument = (document, isForeigner = false, setIsCompany = null, isCNPJAlphanumeric = false) => {
  const documentSize = cnpjCpfSize(document, isCNPJAlphanumeric);
  if (isBRServer && !isForeigner) {
    const isCpf = documentSize === CPF_LENGTH;
    setIsCompany && setIsCompany(!isCpf);

    if (isCpf) {
      return !validateCpf(document) ? 'cpf' : false;
    }

    return !validateCnpj(document, isCNPJAlphanumeric) ? 'cnpj' : false;
  }

  // Para estrangeiros ou servidores não-BR, valida se é alfanumérico
  if (!isAlphaNumeric(document)) {
    return 'invalidDocument';
  }

  return false;
};
